import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import { getAllGames, realTeams } from "@/utils/nba";
import GameCard from "@/components/base/GameCard";
import SectionHeading from "@/components/base/SectionHeading";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import TeamBadge from "@/components/base/TeamBadge";

const TODAY = "2026-10-13";

export default function Home() {
  useSeo({
    title: "NBA直播吧 - 实时NBA篮球赛事直播与比分",
    description:
      "NBA直播吧提供最新NBA赛事直播和实时比分，覆盖常规赛、季后赛及全明星赛，满足篮球迷在线观看比赛和关注赛况的需求。",
    keywords:
      "NBA直播吧,NBA直播,NBA篮球赛事,NBA在线观看,NBA比分直播,NBA赛事直播平台,篮球直播",
  });

  const allGames = getAllGames();
  const todayGames = allGames
    .filter((game) => game.date === TODAY)
    .sort((a, b) => a.time.localeCompare(b.time));

  const featured = [
    ...allGames.filter((game) => game.status === "live"),
    ...allGames.filter((game) => game.status === "upcoming" && game.date === TODAY),
  ].slice(0, 3);

  const latestResults = allGames
    .filter((game) => game.status === "final" && game.season === "2025-2026")
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, 4);

  const hotTeams = realTeams().slice(0, 12);

  return (
    <div>
      <HeroCarousel games={featured} />

      {/* 今日直播赛程 */}
      <section className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <SectionHeading
          icon="ri-live-line"
          title="今日直播赛程"
          subtitle={`${TODAY.replace(/-/g, ".")} 按开赛时间排序 · 共 ${todayGames.length} 场`}
          actionLabel="查看完整赛程"
          actionTo="/category/2025-2026"
        />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {todayGames.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      </section>

      {/* 最新比赛结果 */}
      <section className="bg-background-100">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <SectionHeading
            icon="ri-history-line"
            title="最新比赛结果"
            subtitle="最近结束赛事的比分与关键事件速览"
            actionLabel="查看更多结果"
            actionTo="/replays"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {latestResults.map((game) => (
              <GameCard key={game.id} game={game} variant="result" />
            ))}
          </div>
        </div>
      </section>

      {/* 推荐专题 */}
      <section className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
        <SectionHeading
          icon="ri-article-line"
          title="推荐专题"
          subtitle="赛季前瞻、赛况分析与明星集锦"
          actionLabel="浏览全部专题"
          actionTo="/about"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {topics.slice(0, 6).map((topic) => (
            <Link
              key={topic.id}
              to={topic.link}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-300 cursor-pointer"
            >
              <div className="relative h-44 w-full overflow-hidden">
                <img
                  src={topic.image}
                  alt={topic.title}
                  title={`${topic.title} - NBA直播吧专题`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <span className="absolute left-3 top-3 rounded-md bg-accent-500 px-2 py-0.5 text-xs font-semibold text-background-50">
                  {topic.category}
                </span>
              </div>
              <div className="p-4 md:p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {topic.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{topic.summary}</p>
                <p className="mt-3 text-xs text-foreground-400">{topic.date}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 热门球队 */}
      <section className="bg-primary-800">
        <div className="mx-auto max-w-page px-4 py-10 md:px-6 md:py-14">
          <div className="mb-5 flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-background-50">热门球队</h2>
              <p className="text-xs md:text-sm text-background-100/70">快速进入球队主页，查看阵容与战绩</p>
            </div>
            <Link
              to="/teams"
              className="shrink-0 text-sm font-medium text-accent-300 hover:text-accent-200 flex items-center gap-1 whitespace-nowrap cursor-pointer"
            >
              全部球队
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
            {hotTeams.map((team) => (
              <Link
                key={team.id}
                to={`/team/${team.id}`}
                className="flex flex-col items-center gap-2 rounded-lg bg-primary-700/60 px-3 py-4 transition-colors hover:bg-primary-700 cursor-pointer"
              >
                <TeamBadge team={team} size="md" />
                <span className="text-xs md:text-sm text-background-50 text-center whitespace-nowrap">{team.fullName}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}